/**
 * Cookie utility functions
 */

/**
 * Set a cookie
 * @param {String} name - Cookie name
 * @param {String} value - Cookie value
 * @param {Number} days - Number of days until expiration (default: 7)
 */
export const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  
  // Fallback to localStorage for Safari/ITP compatibility
  try {
    localStorage.setItem(name, value);
  } catch (e) {
    console.error('localStorage set failed:', e);
  }
};

/**
 * Get a cookie value
 * @param {String} name - Cookie name
 * @returns {String|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  // Fallback to localStorage for Safari/ITP compatibility
  try {
    return localStorage.getItem(name);
  } catch (e) {
    return null;
  }
};

/**
 * Delete a cookie
 * @param {String} name - Cookie name
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  
  // Fallback to localStorage for Safari/ITP compatibility
  try {
    localStorage.removeItem(name);
  } catch (e) {
    // Ignore error
  }
};

/**
 * Save userId in cookie
 * @param {String} userId - User ID to save
 */
export const saveUserId = (userId) => {
  setCookie('userId', userId, 7); // Save for 7 days
};

/**
 * Get userId from cookie
 * @returns {String|null} User ID or null if not found
 */
export const getUserId = () => {
  return getCookie('userId');
};

/**
 * Remove userId from cookie
 */
export const removeUserId = () => {
  deleteCookie('userId');
};

/**
 * Save auth token in cookie
 * @param {String} token - JWT token
 */
export const saveAuthToken = (token) => {
  setCookie('authToken', token, 7);
};

/**
 * Get auth token from cookie
 * @returns {String|null}
 */
export const getAuthToken = () => {
  return getCookie('authToken') || getCookie('auth_token');
};

/**
 * Get headers object with Authorization Bearer token for API requests.
 * Use for cross-origin requests where cookies may not be sent.
 */
export const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Remove auth token
 */
export const removeAuthToken = () => {
  deleteCookie('authToken');
  deleteCookie('auth_token');
};

